export const ORDER_STATUSES = [
  'PENDING_PAYMENT',
  'PAID',
  'ACCEPTED',
  'PREPARING',
  'READY',
  'ASSIGNED',
  'PICKED_UP',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'CANCELLED',
  'REFUNDED',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const FULFILLMENT_TYPES = ['DELIVERY', 'PICKUP'] as const;
export type FulfillmentType = (typeof FULFILLMENT_TYPES)[number];

/** Statuses after which nothing else happens to the order (tracking stops). */
export const TERMINAL_STATUSES: readonly OrderStatus[] = ['DELIVERED', 'CANCELLED', 'REFUNDED'];

/** Pickup orders end at PICKED_UP. */
export function isTerminal(status: OrderStatus, type: FulfillmentType): boolean {
  if (TERMINAL_STATUSES.includes(status)) return true;
  return type === 'PICKUP' && status === 'PICKED_UP';
}

/** Statuses where the customer may see the driver's live position. */
export const TRACKABLE_STATUSES: readonly OrderStatus[] = ['PICKED_UP', 'OUT_FOR_DELIVERY'];

const DELIVERY_FLOW: Record<OrderStatus, OrderStatus[]> = {
  PENDING_PAYMENT: ['PAID', 'CANCELLED'],
  PAID: ['ACCEPTED', 'CANCELLED'],
  ACCEPTED: ['PREPARING', 'CANCELLED'],
  PREPARING: ['READY', 'CANCELLED'],
  READY: ['ASSIGNED', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'CANCELLED'],
  ASSIGNED: ['READY', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'CANCELLED'],
  PICKED_UP: ['OUT_FOR_DELIVERY', 'DELIVERED'],
  OUT_FOR_DELIVERY: ['DELIVERED'],
  DELIVERED: ['REFUNDED'],
  CANCELLED: ['REFUNDED'],
  REFUNDED: [],
};

const PICKUP_FLOW: Record<OrderStatus, OrderStatus[]> = {
  PENDING_PAYMENT: ['PAID', 'CANCELLED'],
  PAID: ['ACCEPTED', 'CANCELLED'],
  ACCEPTED: ['PREPARING', 'CANCELLED'],
  PREPARING: ['READY', 'CANCELLED'],
  READY: ['PICKED_UP', 'CANCELLED'],
  ASSIGNED: [],
  PICKED_UP: ['REFUNDED'],
  OUT_FOR_DELIVERY: [],
  DELIVERED: [],
  CANCELLED: ['REFUNDED'],
  REFUNDED: [],
};

export function allowedTransitions(type: FulfillmentType, from: OrderStatus): OrderStatus[] {
  return (type === 'PICKUP' ? PICKUP_FLOW : DELIVERY_FLOW)[from] ?? [];
}

export function canTransition(type: FulfillmentType, from: OrderStatus, to: OrderStatus): boolean {
  return allowedTransitions(type, from).includes(to);
}

/** Transitions a kitchen/staff screen may trigger by hand (the rest come from payment, QR scan or driver). */
export const MANUAL_KITCHEN_TARGETS: readonly OrderStatus[] = ['ACCEPTED', 'PREPARING', 'READY', 'PICKED_UP'];

export const STATUS_LABELS_EN: Record<OrderStatus, string> = {
  PENDING_PAYMENT: 'Awaiting payment',
  PAID: 'New',
  ACCEPTED: 'Accepted',
  PREPARING: 'Preparing',
  READY: 'Ready',
  ASSIGNED: 'Driver assigned',
  PICKED_UP: 'Picked up',
  OUT_FOR_DELIVERY: 'On the way',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
};

export const STATUS_LABELS_FR: Record<OrderStatus, string> = {
  PENDING_PAYMENT: 'Paiement en attente',
  PAID: 'Nouvelle',
  ACCEPTED: 'Acceptée',
  PREPARING: 'En préparation',
  READY: 'Prête',
  ASSIGNED: 'Livreur assigné',
  PICKED_UP: 'Récupérée',
  OUT_FOR_DELIVERY: 'En route',
  DELIVERED: 'Livrée',
  CANCELLED: 'Annulée',
  REFUNDED: 'Remboursée',
};

export const FULFILLMENT_LABELS_FR: Record<FulfillmentType, string> = {
  DELIVERY: 'Livraison',
  PICKUP: 'Ramassage',
};

export const FULFILLMENT_LABELS_EN: Record<FulfillmentType, string> = {
  DELIVERY: 'Delivery',
  PICKUP: 'Pickup',
};

export const STATUS_LABELS = { en: STATUS_LABELS_EN, fr: STATUS_LABELS_FR } as const;
export const FULFILLMENT_LABELS = { en: FULFILLMENT_LABELS_EN, fr: FULFILLMENT_LABELS_FR } as const;
